import type { PositionUpdate, ShipNow, ShipsResponse, TrackPoint, TrailPoint } from "@cruise/core";
import { useEffect, useMemo, useState } from "react";

/** Below this speed a live update marks the ship stopped (same rule as the API). */
const STOPPED_BELOW_KNOTS = 0.5;
/** How long positions from the live stream are kept for trails. */
export const LIVE_TRAIL_MS = 2 * 3600_000;

export async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(path, { headers: { accept: "application/json" }, ...(signal ? { signal } : {}) });
  if (!res.ok) throw new Error(`${path}: HTTP ${res.status}`);
  return (await res.json()) as T;
}

export interface Polled<T> {
  data: T | null;
  error: string | null;
}

/** GET `path` now and every `intervalMs`. A null path clears the data and stops polling. */
export function usePolling<T>(path: string | null, intervalMs: number): Polled<T> {
  const [state, setState] = useState<Polled<T>>({ data: null, error: null });

  useEffect(() => {
    setState({ data: null, error: null });
    if (!path) return;
    const controller = new AbortController();
    const load = () =>
      getJson<T>(path, controller.signal).then(
        (data) => setState({ data, error: null }),
        (err: unknown) => {
          if (controller.signal.aborted) return;
          setState((s) => ({ ...s, error: err instanceof Error ? err.message : String(err) }));
        },
      );
    void load();
    const timer = setInterval(load, intervalMs);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [path, intervalMs]);

  return state;
}

function applyUpdate(ship: ShipNow, update: PositionUpdate | undefined): ShipNow {
  if (!update || (ship.position && update.receivedAt <= ship.position.receivedAt)) return ship;
  return {
    ...ship,
    position: {
      latitude: update.latitude,
      longitude: update.longitude,
      sog: update.sog,
      cog: update.cog,
      heading: update.heading,
      navStatus: ship.position?.navStatus ?? null,
      receivedAt: update.receivedAt,
    },
    minutesSinceLastPosition: 0,
    state: update.sog !== null && update.sog < STOPPED_BELOW_KNOTS ? "stopped" : "under-way",
  };
}

export function toTrailPoint(point: TrackPoint): TrailPoint {
  return [point.latitude, point.longitude, Date.parse(point.receivedAt)];
}

/**
 * A trail from the server, extended with live points received after its last
 * point, without points older than `sinceMs`.
 */
export function joinTrail(
  base: readonly TrailPoint[],
  live: readonly TrailPoint[] | undefined,
  sinceMs = -Infinity,
): TrailPoint[] {
  const lastMs = base[base.length - 1]?.[2] ?? -Infinity;
  return [...base, ...(live ?? []).filter((p) => p[2] > lastMs)].filter((p) => p[2] >= sinceMs);
}

export interface LiveShips {
  ships: ShipNow[];
  error: string | null;
  /** Whether the /v1/stream connection is open. */
  streamOpen: boolean;
  /** Positions from the stream in the last 2 hours, by IMO, oldest first. */
  live: ReadonlyMap<string, readonly TrailPoint[]>;
}

/**
 * Fleet ships from GET /v1/ships (reloaded every 60 s), with positions moved
 * as soon as GET /v1/stream reports them.
 */
export function useLiveShips(): LiveShips {
  const polled = usePolling<ShipsResponse>("/v1/ships", 60_000);
  const [updates, setUpdates] = useState<ReadonlyMap<string, PositionUpdate>>(new Map());
  const [live, setLive] = useState<ReadonlyMap<string, readonly TrailPoint[]>>(new Map());
  const [streamOpen, setStreamOpen] = useState(false);

  useEffect(() => {
    // EventSource reconnects by itself; missed updates arrive with the next 60 s reload.
    const source = new EventSource("/v1/stream");
    source.addEventListener("open", () => setStreamOpen(true));
    source.addEventListener("error", () => setStreamOpen(false));
    source.addEventListener("positions", (event) => {
      const batch = JSON.parse((event as MessageEvent<string>).data) as PositionUpdate[];
      setUpdates((prev) => {
        const next = new Map(prev);
        for (const u of batch) next.set(u.imo, u);
        return next;
      });
      setLive((prev) => {
        const cutoff = Date.now() - LIVE_TRAIL_MS;
        const next = new Map<string, readonly TrailPoint[]>();
        for (const [imo, points] of prev) {
          const kept = points[points.length - 1]![2] >= cutoff ? points.filter((p) => p[2] >= cutoff) : [];
          if (kept.length > 0) next.set(imo, kept);
        }
        for (const u of batch) {
          const points = next.get(u.imo) ?? [];
          const ms = Date.parse(u.receivedAt);
          if (ms > (points[points.length - 1]?.[2] ?? -Infinity)) next.set(u.imo, [...points, [u.latitude, u.longitude, ms]]);
        }
        return next;
      });
    });
    return () => source.close();
  }, []);

  const ships = useMemo(
    () => (polled.data?.ships ?? []).map((ship) => applyUpdate(ship, updates.get(ship.imo))),
    [polled.data, updates],
  );
  return { ships, error: polled.error, streamOpen, live };
}
